import React from "react";
import { getStock, getStockConfig, type StockData, type GridConfig, stockOut } from "../../api/stock";
import Stock from "./Grid";
import { type Breadcrumb, useCrumbs } from "../../component/breadcrumbs";
import useLocale from "../../util/i18n";
import { MDBBadge, MDBBtn, MDBInput, MDBInputGroup, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader } from "mdb-react-ui-kit";
import { saveConfig } from "../../api/core";
import Toast from "../../component/toast";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { faDolly, faMagnifyingGlass, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { parseSku } from "../../util/misc";
import BigNumber from "bignumber.js";
import { getStockQty, type SkuQty } from "../../api/erp";
import ExcelJS from "exceljs";
import { format } from "date-fns";
import { GridContext } from "./_misc";

export default function Rack() {
    let { 0: config, 1: setConfig } = React.useState<Array<GridConfig>>([]);
    let { 0: status, 1: setStatus } = React.useState<Record<number, Record<number, number>>>({});
    let { 0: search, 1: setSearch } = useSearchParams();
    let picking = React.useMemo(() => {
        let result: Record<number, Array<number>> = {};
        for (let area in config) {
            if (config[area].picking?.length) {
                result[parseInt(area)] = config[area].picking!;
            }
        }
        return result;
    }, [config]);
    let area = React.useMemo(() => parseInt(search.get('area') ?? '-1'), [search]);
    let { 0: setCrumbs } = useCrumbs();
    let { 0: __ } = useLocale();
    let change = React.useCallback((config: GridConfig | null, index?: number) => {
        return new Promise<void>((resolve, reject) => {
            setConfig(old => {
                let bak = [...old];
                let cloned = [...old];
                if (config) {
                    if ((index ?? area) < 0) {
                        cloned.push(config);
                    } else {
                        cloned[index ?? area] = config;
                    }
                } else {
                    cloned.splice(index ?? area, 1);
                }
                saveConfig({ path: 'stock/box', value: JSON.stringify(cloned) }).then(() => resolve(), () => {
                    Toast.show(__('An error detected. Please try again later.'), 'danger');
                    setConfig(bak);
                    reject();
                });
                return cloned;
            });
        });
    }, [config, setConfig, area, __]);
    let view = React.useCallback((area: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setSearch({ area: area.toString() });
    }, [setSearch]);
    let setMark = React.useCallback((status: number, value: number) => {
        setStatus(old => {
            let cloned = { ...old };
            cloned[area] ??= {};
            cloned[area][status] = (cloned[area][status] ?? 0) + value;
            return cloned;
        });
    }, [setStatus, area]);
    const editor = React.useRef<ConfigRef>(null);
    const edit = React.useCallback((item: GridConfig, i: number) => {
        editor.current?.(item, i);
    }, []);
    React.useEffect(() => {
        getStockConfig({ status: 1 }).then(response => {
            if (typeof response !== 'string') {
                setConfig(response.config);
                let status: Record<number, Record<number, number>> = {};
                for (let item of response.status) {
                    status[item.area] ??= {};
                    status[item.area][item.status] = (status[item.area][item.status] ?? 0) + 1;
                }
                setStatus(status);
            }
        });
    }, [setConfig, setStatus]);
    React.useEffect(() => {
        let crumbs: Array<Breadcrumb> = ['Bin Location'];
        if (area >= 0) {
            crumbs = [{ link: '/bin', label: 'Bin Location' }];
            config[area]?.name && crumbs.push(config[area].name!);
        }
        setCrumbs(crumbs);
    }, [config, setCrumbs, area]);
    React.useEffect(() => {
        if (area >= 0 && config[area]) {
            return;
        }
        let l = () => {
            getStock({}).then(async response => {
                if (typeof response !== 'string') {
                    let data = response.data;
                    data.sort((a, b) => a.area - b.area || a.row - b.row || a.col - b.col);
                    let picks: Record<string, true> = {}, sum: Record<string, BigNumber> = {}, fixed: Record<string, number> = {};
                    let ps: Array<Promise<void>> = [];
                    for (let item of data) {
                        ps.push((async () => {
                            let sku = await parseSku(item.sku);
                            if (picking[item.area]?.includes(item.row) === true) {
                                picks[sku] = true;
                            } else {
                                sum[sku] = (sum[sku] ?? new BigNumber(0)).minus(item.qty);
                            }
                        })());
                    }
                    ps.length && await Promise.all(ps);
                    let sku = Object.keys(picks);
                    if (sku.length) {
                        for (let item of (await getStockQty({ filter: { sku } }) as { data: Array<SkuQty> })?.data ?? []) {
                            fixed[item.sku] = (sum[item.sku] ?? new BigNumber(0)).plus(item.qty).toNumber();
                        }
                    }
                    const file = new ExcelJS.Workbook();
                    let exists: Record<string, true> = {};
                    for (let i in config) {
                        let name = config[i].name || __('Area %d').replace('%d', (i + 1).toString()), j = '';
                        while (exists[name + j]) {
                            j = ' ' + (parseInt(j.substring(1) || '0') + 1).toString();
                        }
                        exists[name + j] = true;
                        const sheet = file.addWorksheet(name + j);
                        let row = sheet.getRow(1);
                        row.getCell(1).value = __('Row');
                        row.getCell(2).value = __('Column');
                        row.getCell(3).value = 'SKU';
                        row.getCell(4).value = __('Qty');
                    }
                    for (let item of data) {
                        const sheet = file.getWorksheet(parseInt(item.area.toString()) + 1);
                        if (sheet) {
                            let row = sheet.getRow(sheet.rowCount + 1), isPicking = picking[item.area]?.includes(item.row) === true, sku = await parseSku(item.sku);
                            row.getCell(1).value = (parseInt(item.row.toString()) + 1).toString() + (isPicking ? '[P]' : '');
                            row.getCell(2).value = parseInt(item.col.toString()) + 1;
                            row.getCell(3).value = sku;
                            row.getCell(4).value = isPicking ? (fixed[sku] || 0) : parseFloat(item.qty.toString());
                            if (isPicking) {
                                if (item.status === 1) {
                                    row.getCell(4).style = { font: { color: { argb: 'FF33CC66' } } };
                                } else if (item.status === 2) {
                                    row.getCell(4).style = { font: { color: { argb: 'FFE4A11B' } } };
                                } else if ((fixed[sku] || 0) <= 0) {
                                    row.getCell(4).style = { font: { color: { argb: 'FFCC3333' } } };
                                }
                            }
                        }
                    }
                    file.xlsx.writeBuffer().then(buffer => {
                        let a = document.createElement('a');
                        a.download = 'export.xlsx';
                        a.href = URL.createObjectURL(new Blob([buffer]));
                        document.body.append(a);
                        a.click();
                        URL.revokeObjectURL(a.href);
                        a.remove();
                    });
                }
            });
        };
        document.addEventListener('export', l);
        return () => {
            document.removeEventListener('export', l);
        };
    }, [config, picking, area]);
    return (
        <GridContext.Provider value={[config, change]}>
            {area >= 0 && config[area] ? (<Stock area={area} setMark={setMark} />) : (
                <ul className="racks row" data-searchable="1">
                    {config.map((item, i) => (
                        <li key={(item.name ?? '') + i} className="col-12 col-md-6 col-xl-4 col-xxl-3">
                            <div className="rack">
                                <h3 className="name">{item.name ?? __('Area %d').replace('%d', (i + 1).toString())}</h3>
                                <p className="size">
                                    {item.rows + 'x' + item.cols}
                                    <MDBBtn size="sm" color="link" onClick={edit.bind(null, item, i)}><FontAwesomeIcon icon={faEdit} /></MDBBtn>
                                </p>
                                <p className="status">{Object.keys(status[i] ?? {}).map((key: string) => ((status[i][parseInt(key)] ?? 0) > 0) ? (
                                    <MDBBadge key={i + '-' + key} color={key === '2' ? 'warning' : (key === '1' ? 'success' : 'danger')}>{status[i][parseInt(key)] ?? 0}</MDBBadge>
                                ) : null)}</p>
                                <MDBBtn onClick={view.bind(null, i)} className="view">{__('View')}</MDBBtn>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <Config ref={editor} />
            <Search />
        </GridContext.Provider>
    );
}

type ConfigRef = (config: GridConfig, i: number) => void;

const Config = React.forwardRef<ConfigRef>((_, ref) => {
    let { 0: data, 1: setData } = React.useState<Partial<GridConfig> | null>(null);
    let { 0: area, 1: setArea } = React.useState(-1);
    let { 0: loading, 1: setLoading } = React.useState(false);
    let { 0: __ } = useLocale();
    let { 1: save } = React.useContext(GridContext);
    let onChange = React.useCallback((key: 'name' | 'rows' | 'cols', e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setData(old => {
            let cloned = { ...(old ?? {}) };
            let value = e.target.value || '';
            if (key !== 'name') {
                let n = parseFloat(value);
                //@ts-ignore
                cloned[key] = Number.isNaN(n) ? value : n;
            } else {
                cloned[key] = value;
            }
            return cloned;
        });
    }, [setData]);
    let onSubmit = React.useCallback((e: React.FormEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (typeof data?.cols === 'string') {
            data.cols = parseInt(data.cols) || 10;
        }
        if (typeof data?.rows === 'string') {
            data.rows = parseInt(data.rows) || 10;
        }
        setLoading(true);
        save(data as GridConfig, area).then(() => {
            setLoading(false);
            setData(null);
        });
    }, [setData, data, save, setLoading, area]);
    let onDelete = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setLoading(true);
        save(null, area).then(() => {
            setLoading(false);
            setData(null);
        });
    }, [setData, save, setLoading, area]);
    React.useImperativeHandle(ref, () => (
        (config, area) => {
            setData(config);
            setArea(area);
        }
    ), [setData]);
    React.useEffect(() => {
        let l = () => {
            setData({ rows: 10, cols: 10 });
            setArea(-1);
        };
        document.addEventListener('new', l);
        return () => {
            document.removeEventListener('new', l);
        };
    }, [setData]);
    return (
        <>
            <div hidden data-new-url="#" data-exportable="1" />
            <MDBModal open={Boolean(data)} onClose={setData.bind(null, null)} tabIndex="-1">
                <MDBModalDialog centered size="sm">
                    <MDBModalContent tag="form" onSubmit={onSubmit}>
                        <MDBModalBody>
                            <MDBInput name="name" value={data?.name ?? ''} label={__('Name')} className="active" onChange={onChange.bind(null, 'name')} />
                            <MDBInput type="number" name="rows" value={data?.rows ?? ''} min={1} step={1} label={__('Rows')} className="active my-3" required onChange={onChange.bind(null, 'rows')} />
                            <MDBInput type="number" name="cols" value={data?.cols ?? ''} min={1} step={1} label={__('Columns')} className="active" required onChange={onChange.bind(null, 'cols')} />
                        </MDBModalBody>
                        <MDBModalFooter>
                            {area < 0 ? null : (<MDBBtn type="button" color="danger" onClick={onDelete} size="sm">{__('Delete')}</MDBBtn>)}
                            <MDBBtn type="button" color="secondary" onClick={setData.bind(null, null)} size="sm">{__('Close')}</MDBBtn>
                            <MDBBtn type="submit" disabled={loading} size="sm">{__('Save')}</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </>
    );
});

const year = (new Date()).getFullYear();

function Search() {
    let { 0: config } = React.useContext(GridContext);
    let { 0: status, 1: setStatus } = React.useState(false);
    let { 0: data, 1: setData } = React.useState<Array<StockData & { isPicking: boolean }>>([]);
    let { 0: loading, 1: setLoading } = React.useState(false);
    let { 0: __, 3: locale } = useLocale();
    let fallback = React.useRef<StockData & { isPicking: boolean } | null>(null);
    let picking = React.useMemo(() => {
        let result: Record<number, Array<number>> = {};
        for (let area in config) {
            if (config[area].picking?.length) {
                result[parseInt(area)] = config[area].picking!;
            }
        }
        return result;
    }, [config]);
    let doStockOut = React.useCallback((i: number, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (window.confirm(__('Are you sure to delete this?'))) {
            setData(old => {
                const cloned = [ ...old ];
                let item = fallback.current = { ...cloned[i] };
                cloned.splice(i, 1);
                stockOut({ position: [[item.area, item.row, item.col, item.sku]] }).catch(() => {
                    setData(old => ([...old.slice(0, i), fallback.current!, ...old.slice(i)]));
                });
                return cloned;
            });
        }
    }, [setData, __]);
    let onSubmit = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        let code = (new FormData(e.target as HTMLFormElement)).get('sku') as string | null;
        if (code) {
            setLoading(true);
            getStock(code.trim()).then(async response => {
                if (typeof response !== 'string') {
                    let result: Array<StockData & { isPicking: boolean }> = [], sum: Record<string, BigNumber> = {}, picks: Record<string, Array<string>> = {};
                    let ps: Array<Promise<void>> = [];
                    for (let i in response.data) {
                        ps.push((async () => {
                            let item = response.data[i], isPicking = picking[item.area]?.includes(item.row) === true;
                            let sku = await parseSku(item.sku);
                            if (isPicking) {
                                picks[sku] ??= [];
                                picks[sku].push(i);
                            } else {
                                sum[sku] = (sum[sku] ?? new BigNumber(0)).minus(item.qty);
                            }
                            result.push({ ...item, isPicking });
                        })());
                    }
                    ps.length && await Promise.all(ps);
                    let sku = Object.keys(picks);
                    if (sku.length) {
                        for (let item of (await getStockQty({ filter: { sku } }) as { data: Array<SkuQty> })?.data ?? []) {
                            for (let idx of picks[item.sku]) {
                                result[parseInt(idx)].qty = (sum[item.sku] ?? new BigNumber(0)).plus(item.qty).toNumber();
                            }
                        }
                    }
                    result.sort((a, b) => (a.created_at ?? 0) - (b.created_at ?? 0));
                    setLoading(false);
                    setData(result);
                }
            });
        }
    }, [setData, picking, setLoading]);
    React.useEffect(() => {
        let l = setStatus.bind(null, true);
        document.addEventListener('search', l);
        return () => document.removeEventListener('search', l);
    }, [setStatus]);
    return (
        <MDBModal open={status} onClose={setStatus.bind(null, false)} tabIndex="-1">
            <MDBModalDialog centered size="sm">
                <MDBModalContent tag="form" onSubmit={onSubmit}>
                    <MDBModalHeader>
                        <MDBInputGroup>
                            <MDBInput name="sku" label="SKU" className="active" autoCapitalize="characters" />
                            <MDBBtn type="submit">
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </MDBBtn>
                        </MDBInputGroup>
                    </MDBModalHeader>
                    {loading ? (
                        <MDBModalBody>
                            <FontAwesomeIcon className="d-block mx-auto" icon={faSpinner} spin size="3x" />
                        </MDBModalBody>
                    ) : (data.length ? (
                        <MDBModalBody>
                            <ul className="bin-list">
                                {data.map((item, i) => {
                                    let date = item.created_at ? new Date(item.created_at * 1000) : null;
                                    return (
                                        <li key={item.sku + '-' + i}>
                                            <span className="sku">{item.sku}</span>
                                            <div className="badges">
                                                {date ? (
                                                    <small className="date">{format(date, (date.getFullYear() !== year ? 'PPP' : 'LLL do'), { locale })}</small>
                                                ) : null}
                                                {item.status === 1 ? (
                                                    <MDBBadge color={'success'}>1</MDBBadge>
                                                ) : (item.status === 2 ? (
                                                    <MDBBadge color={'warning'}>1</MDBBadge>
                                                ) : null)}
                                                <MDBBadge color={item.qty < 0 ? 'danger' : 'primary'}>{parseFloat(item.qty.toString())}</MDBBadge>
                                            </div>
                                            <Link to={'/bin?area=' + item.area + '#' + item.row + 'x' + item.col} className="location">
                                                {config[item.area].name ?? __('Area %d').replace('%d', (item.area + 1).toString())}:
                                                {item.row + 1}x{item.col + 1}
                                                {item.isPicking ? (
                                                    <span className="ms-2 text-danger">[P]</span>
                                                ) : null}
                                            </Link>
                                            <MDBBtn type="button" color="danger" size="sm" onClick={doStockOut.bind(null, i)}>
                                                <FontAwesomeIcon icon={faDolly} />
                                            </MDBBtn>
                                        </li>
                                    );
                                })}
                            </ul>
                        </MDBModalBody>
                    ) : null)}
                    <MDBModalFooter>
                        <MDBBtn color="secondary" onClick={setStatus.bind(null, false)}>{__('Close')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}
